import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem,
  Stack, Typography, Alert, CircularProgress, InputAdornment, Box, Divider,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { doc, writeBatch, increment, serverTimestamp, Timestamp, arrayUnion } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import {
  CLP, FINANCE_COLORS, MEDIOS_PAGO, labelMedio, netoDesdeBruto, ivaDesdeBruto,
} from '../../utils/finance';

const { PRIMARY } = FINANCE_COLORS;

// Campo acumulador del turno según el medio de pago (mismo mapa que ventaService)
const CAMPO_TURNO_POR_MEDIO = {
  efectivo: 'totalEfectivo',
  debito: 'totalDebito',
  credito: 'totalCredito',
  transferencia: 'totalTransferencia',
};
const campoTurno = (medio) => CAMPO_TURNO_POR_MEDIO[medio] || 'totalOtros';

const totalDeVenta = (v) => Number(v?.totalFinal ?? v?.total ?? 0);
const medioDeVenta = (v) => v?.pagos?.[0]?.medio || v?.paymentMethod || 'otro';

// Reescala los ítems para que sigan sumando el nuevo total. Así el Informe
// Financiero (que agrupa por ítem) se mantiene consistente con la venta.
function reescalarItems(items, totalAnterior, totalNuevo) {
  const list = Array.isArray(items) ? items : [];
  if (list.length === 0) return list;

  if (list.length === 1) {
    const it = list[0];
    const cantidad = Number(it.cantidad) || 1;
    return [{
      ...it,
      precioUnitario: Math.round(totalNuevo / cantidad),
      subtotal: netoDesdeBruto(totalNuevo),
      iva: ivaDesdeBruto(totalNuevo),
      total: totalNuevo,
    }];
  }

  // Sin base para prorratear: se deja tal cual para no inventar montos
  if (!totalAnterior) return list;

  const factor = totalNuevo / totalAnterior;
  let acumulado = 0;
  return list.map((it, i) => {
    const esUltimo = i === list.length - 1;
    const total = esUltimo
      ? totalNuevo - acumulado
      : Math.round((Number(it.total) || 0) * factor);
    acumulado += total;
    const cantidad = Number(it.cantidad) || 1;
    return {
      ...it,
      precioUnitario: Math.round(total / cantidad),
      descuento: Math.round((Number(it.descuento) || 0) * factor),
      subtotal: netoDesdeBruto(total),
      iva: ivaDesdeBruto(total),
      total,
    };
  });
}

export default function EditVentaDialog({ open, onClose, venta, turno, onSaved }) {
  const { currentUser, userData } = useAuth();
  const [monto, setMonto] = useState('');
  const [medio, setMedio] = useState('');
  const [motivo, setMotivo] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const totalAnterior = totalDeVenta(venta);
  const medioAnterior = medioDeVenta(venta);

  useEffect(() => {
    if (!open || !venta) return;
    setMonto(String(totalAnterior));
    setMedio(medioAnterior);
    setMotivo('');
    setError('');
  }, [open, venta, totalAnterior, medioAnterior]);

  const totalNuevo = Math.round(Number(monto) || 0);
  const hayCambios = totalNuevo !== totalAnterior || medio !== medioAnterior;

  const preview = useMemo(() => ({
    neto: netoDesdeBruto(totalNuevo),
    iva: ivaDesdeBruto(totalNuevo),
  }), [totalNuevo]);

  const handleGuardar = async () => {
    if (totalNuevo <= 0) { setError('El monto debe ser mayor a cero.'); return; }
    if (!medio) { setError('Selecciona un medio de pago.'); return; }
    if (!hayCambios) { setError('No hay cambios por guardar.'); return; }
    if (!motivo.trim()) { setError('Indica el motivo de la modificación.'); return; }

    setSaving(true); setError('');
    try {
      const neto = netoDesdeBruto(totalNuevo);
      const iva = ivaDesdeBruto(totalNuevo);
      const pagoBase = venta.pagos?.[0] || {};

      const batch = writeBatch(db);

      batch.update(doc(db, 'Sales', venta.id), {
        total: totalNuevo,
        totalFinal: totalNuevo,
        subtotal: neto,
        subtotalNeto: neto,
        iva,
        montoIVA: iva,
        paymentMethod: medio,
        pagos: [{
          medio,
          monto: totalNuevo,
          referencia: pagoBase.referencia || null,
          cuotas: pagoBase.cuotas || null,
        }],
        items: reescalarItems(venta.items, totalAnterior, totalNuevo),
        editadoAt: serverTimestamp(),
        editadoBy: currentUser?.uid || '',
        editadoByName: userData?.name || currentUser?.email || '',
        historialEdiciones: arrayUnion({
          at: Timestamp.now(),
          por: userData?.name || currentUser?.email || '',
          porId: currentUser?.uid || '',
          totalAnterior,
          totalNuevo,
          medioAnterior,
          medioNuevo: medio,
          motivo: motivo.trim(),
        }),
      });

      // Reajustar los acumuladores del turno abierto
      if (turno?.id) {
        const deltas = {
          totalBruto: increment(totalNuevo - totalAnterior),
          totalNeto: increment(neto - (Number(venta.subtotalNeto ?? venta.subtotal) || 0)),
          totalIVA: increment(iva - (Number(venta.montoIVA ?? venta.iva) || 0)),
        };
        const campoAnterior = campoTurno(medioAnterior);
        const campoNuevo = campoTurno(medio);
        if (campoAnterior === campoNuevo) {
          deltas[campoNuevo] = increment(totalNuevo - totalAnterior);
        } else {
          deltas[campoAnterior] = increment(-totalAnterior);
          deltas[campoNuevo] = increment(totalNuevo);
        }
        batch.update(doc(db, 'turnosCaja', turno.id), deltas);
      }

      await batch.commit();
      onSaved?.();
    } catch (e) {
      setError('Error al guardar los cambios: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, color: PRIMARY }}>Editar venta</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {venta?.ownerName || venta?.patientName || 'Venta'}
          {venta?.folioDocumento || venta?.boletaNumber
            ? ` · Boleta ${venta.folioDocumento || venta.boletaNumber}`
            : ''}
        </Typography>

        <Stack spacing={2}>
          <TextField
            fullWidth type="number" label="Monto total (IVA incluido)" value={monto}
            onChange={(e) => setMonto(e.target.value)} InputLabelProps={{ shrink: true }}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            helperText={`Monto actual: ${CLP(totalAnterior)}`}
          />
          <TextField
            select fullWidth label="Medio de pago" value={medio}
            onChange={(e) => setMedio(e.target.value)} InputLabelProps={{ shrink: true }}
            helperText={`Medio actual: ${labelMedio(medioAnterior)}`}
          >
            {MEDIOS_PAGO.map((m) => <MenuItem key={m.id} value={m.id}>{m.label}</MenuItem>)}
          </TextField>
          <TextField
            fullWidth multiline minRows={2} label="Motivo de la modificación" value={motivo}
            onChange={(e) => setMotivo(e.target.value)} InputLabelProps={{ shrink: true }}
            required helperText="Queda registrado en el historial de la venta"
          />
        </Stack>

        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">Neto</Typography>
          <Typography variant="body2">{CLP(preview.neto)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">IVA</Typography>
          <Typography variant="body2">{CLP(preview.iva)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
          <Typography variant="body2" fontWeight={700}>Total</Typography>
          <Typography variant="body2" fontWeight={700}>{CLP(totalNuevo)}</Typography>
        </Box>

        {(venta?.items || []).length > 1 && totalNuevo !== totalAnterior && (
          <Alert severity="info" sx={{ mt: 2 }}>
            La venta tiene {venta.items.length} ítems: los montos se prorratearán
            proporcionalmente para que sigan sumando el nuevo total.
          </Alert>
        )}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button
          variant="contained" onClick={handleGuardar} disabled={saving || !hayCambios}
          startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
          sx={{ bgcolor: PRIMARY, fontWeight: 700 }}
        >
          {saving ? 'Guardando…' : 'Guardar cambios'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
